import { Indicator, Tabs } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ErrorBoundary } from "@sentry/react";
import { t } from "i18next";
import { Fragment, Suspense } from "react";

import { safeLazy } from "#shared/helpers/safeLazy";
import { getFirst2DFile, get3DFile, JobStatusSchema } from "shared";

const Viewer3D = safeLazy(() =>
  import("#shared/view/components/Thumbnails/ThumbnailViewer/Viewer3D").then((m) => ({
    default: m.Viewer3D,
  })),
);
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { Modal } from "#shared/view/components/Modal";
import { Text } from "#shared/view/components/Text";
import { AnalysisResult } from "#shared/view/components/Thumbnails/AnalysisResult/AnalysisResult";
import { AnalyseThumbnail } from "#shared/view/components/Thumbnails/AnalyzeThumbnail";
import { ErrorThumbnail } from "#shared/view/components/Thumbnails/ErrorThumbnail";
import { Thumbnail } from "#shared/view/components/Thumbnails/Thumbnail";

type ThumbnailProps = {
  part: PartDto | undefined;
  size?: { width: number | string; height: number | string };
  onClick?: () => void;
  quoteRequestLineId?: string;
  opened?: boolean;
  onClose?: () => void;
  defaultTab?: string | null;
};

export const ThumbnailOverlay = ({
  part,
  size,
  onClick,
  quoteRequestLineId,
  opened,
  onClose,
  defaultTab,
}: ThumbnailProps) => {
  const [isVisible, { close, open: openViewer }] = useDisclosure(false);
  const effectiveOpened = opened || isVisible;
  const handleClose = () => {
    close();
    onClose?.();
  };

  if (!part) {
    return <Thumbnail size={size} />;
  }

  const isPartPreviewProcessing =
    part.previewStatus === JobStatusSchema.enum.PROCESSING ||
    part.previewStatus === JobStatusSchema.enum.STANDBY;

  if (isPartPreviewProcessing) {
    return <AnalyseThumbnail size={size} />;
  }

  const file3D = get3DFile(part.files);
  const first2DFile = getFirst2DFile(part.files);

  const has2DFile = !!first2DFile;
  const effectivePdfUrl = part.urlPdfAnnotated ?? part.urlPdfPreview;

  const hasPreviewFile = !!part.urlPng3D || !!part.urlPng2D;
  const has3DPreviewError = part.gltfStatus === JobStatusSchema.enum.ERROR;
  const hasPreviewError = part.previewStatus === JobStatusSchema.enum.ERROR;

  if (!has2DFile && (hasPreviewError || !hasPreviewFile)) {
    return (
      <ErrorThumbnail
        size={size}
        icon={"alertTriangle"}
        errorMessage={t("qrl.preview.tooltip.png")}
        part={part}
      />
    );
  }

  if (!has2DFile && has3DPreviewError) {
    return <ErrorThumbnail size={size} errorMessage={t("qrl.preview.tooltip.gltf")} part={part} />;
  }

  return (
    <Fragment>
      <Modal
        data-testid="viewer-3d"
        opened={effectiveOpened}
        onClose={handleClose}
        onClick={(e) => {
          e.stopPropagation();
        }}
        centered
        title={
          <Text fw={700} fz={16} truncate>
            {part.name}
          </Text>
        }
        size="90vw"
        styles={{ content: { width: "90vw" } }}
        transitionProps={{ transition: "fade", duration: 200 }}
      >
        <Tabs
          key={defaultTab}
          defaultValue={defaultTab ?? (file3D ? "3d" : "pdf")}
          keepMounted={false}
        >
          <Tabs.List>
            {file3D && <Tabs.Tab value="3d">3D</Tabs.Tab>}
            {has2DFile && <Tabs.Tab value="pdf">2D / PDF</Tabs.Tab>}
          </Tabs.List>
          {file3D && (
            <Tabs.Panel value="3d" style={{ height: "80vh" }}>
              {has3DPreviewError ? (
                <ErrorThumbnail
                  size={size}
                  errorMessage={t("qrl.preview.tooltip.gltf")}
                  part={part}
                />
              ) : (
                <ErrorBoundary fallback={<></>}>
                  <Suspense>
                    <Viewer3D style={{ height: "100%", margin: 0 }} urlGltf={part.urlGltf} />
                  </Suspense>
                </ErrorBoundary>
              )}
            </Tabs.Panel>
          )}
          {has2DFile && (
            <Tabs.Panel value="pdf" style={{ height: "80vh" }}>
              <AnalysisResult
                part={part}
                pdfUrl={effectivePdfUrl}
                quoteRequestLineId={quoteRequestLineId}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </Modal>
      <Indicator
        inline
        label="2D"
        zIndex={0}
        styles={{
          indicator: {
            fontSize: 8,
          },
        }}
        color="grey.5"
        disabled={!has2DFile}
        processing={part.analyze2DStatus === JobStatusSchema.enum.PROCESSING}
      >
        <Thumbnail part={part} size={size} cursor="zoom-in" onClick={onClick || openViewer} />
      </Indicator>
    </Fragment>
  );
};
